import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Ring } from "@uiball/loaders";
import { useJsApiLoader } from "@react-google-maps/api";
import { addProductTrackingDetail, getProductInformation, addTrackingDescription, addCertificate } from "../../../../utils/services/user.service";
import { getExplorerTxBase } from "../../../../utils/blockchainExplorer";
import LocationSearch from "./LocationSearch";
import PhaseHeader from "./traceability/PhaseHeader";
import TrackingEventCard from "./traceability/TrackingEventCard";
import TrackingHistorySidebar from "./traceability/TrackingHistoryModal";
import { LoadingOverlay, ActionWindowCard, ActionWindowLabel, DateRow, DateChip, DateDot, CountdownChip } from "./traceability/TraceabilityStyles";
import { FormContainer, FormGroup, Label, Input, TextArea, FileInputWrapper, FileInputLabel, FileInput, FileList, SubmitButton, DescriptionSidebar, CertificationSidebar, SidebarOverlay, SidebarHeader, SidebarTitle, CloseButton } from "./traceability/FormStyles";

function TraceabilityPROV4({ productId, productData, adminMode = false, isPublishLocked = false, actionStart, actionEnd }) {
  const [chain, setChain] = useState('BNB');
  const [showTrackingForm, setShowTrackingForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [trackingDetails, setTrackingDetails] = useState([]);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [showDescriptionSidebar, setShowDescriptionSidebar] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [descriptionFormData, setDescriptionFormData] = useState({ description: "", location: "" });
  const [descriptionFiles, setDescriptionFiles] = useState([]);
  const [isSubmittingDescription, setIsSubmittingDescription] = useState(false);
  const [descriptionLocationData, setDescriptionLocationData] = useState(null);
  const [descriptionCoordinates, setDescriptionCoordinates] = useState(null);
  const [showCertificationSidebar, setShowCertificationSidebar] = useState(false);
  const [certificationFiles, setCertificationFiles] = useState([]);
  const [isSubmittingCertification, setIsSubmittingCertification] = useState(false);
  const [certificationFormData, setCertificationFormData] = useState({ title: "", description: "", location: "" });
  const [certificationLocationData, setCertificationLocationData] = useState(null);
  const [certificationCoordinates, setCertificationCoordinates] = useState(null);
  const [formData, setFormData] = useState({ title: "", description: "", batch_number: "", location: "" });
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [coordinates, setCoordinates] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedRecordForHistory, setSelectedRecordForHistory] = useState(null);
  const [trackingHistory, setTrackingHistory] = useState({ descriptions: [], certificates: [] });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyCYx6E7TSAHDQMFB41DNksmX7v3zXaKt5Y',
    libraries: ['places'],
    id: 'traceability-maps-loader',
  });

  const txBase = getExplorerTxBase(chain);

  const phaseMeta = {
    Mint: { icon: '🧬', hint: 'Tokenization & genesis record' },
    QC: { icon: '🧪', hint: 'Verification & quality checks' },
    Vault: { icon: '🧊', hint: 'Custody / storage events' },
    Transfer: { icon: '🔁', hint: 'Ownership / logistics / settlement' },
  };

  const isProductOwner = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const currentUserId = user?.data?.user?.user_id || user?.data?.user?._id;
      const productUserId = productData?.userId || productData?.current_owner?.[0]?.current_owner_id;
      return currentUserId && productUserId && currentUserId === productUserId;
    } catch (error) {
      console.error("Error checking product owner:", error);
      return false;
    }
  };

  const parseLocation = (locationArray) => {
    if (!locationArray || !Array.isArray(locationArray) || locationArray.length === 0) return null;
    try {
      const locationStr = locationArray[0];
      const locationObj = typeof locationStr === 'string' ? JSON.parse(locationStr) : locationStr;
      return { lat: locationObj.lat, lng: locationObj.lng };
    } catch (error) {
      console.error("Error parsing location:", error);
      return null;
    }
  };

  const mapTrackingToEvents = (details) => {
    if (!Array.isArray(details) || details.length === 0) return [];
    return details.map((detail, index) => {
      let phase = 'Transfer';
      const titleLower = (detail.title || '').toLowerCase();
      if (titleLower.includes('mint') || titleLower.includes('genesis') || titleLower.includes('tokenize')) {
        phase = 'Mint';
      } else if (titleLower.includes('quality') || titleLower.includes('qc') || titleLower.includes('verify') || titleLower.includes('check')) {
        phase = 'QC';
      } else if (titleLower.includes('vault') || titleLower.includes('storage') || titleLower.includes('custody')) {
        phase = 'Vault';
      }
      let status = 'info';
      if (titleLower.includes('pass') || titleLower.includes('confirm') || titleLower.includes('complete')) {
        status = 'ok';
      } else if (titleLower.includes('pending') || titleLower.includes('wait')) {
        status = 'pending';
      }
      return {
        id: detail._id || `event-${index}`,
        phase,
        date: detail.createdAt || new Date().toISOString(),
        title: detail.title || `Tracking Event ${index + 1}`,
        status,
        actor: detail.origin || 'System',
        location: detail.location ? (typeof detail.location === 'string' ? detail.location : JSON.parse(detail.location[0])?.location || 'Unknown') : 'Unknown',
        tx: detail.hash || null,
        detail: detail.description || '',
        batch_number: detail.batch_number || '',
        images: detail.images || [],
        documents: detail.documents || [],
        coordinates: parseLocation(detail.location),
        rawData: detail,
      };
    });
  };

  const fetchTrackingDetails = async () => {
    if (!productId) return;
    try {
      setIsLoadingTracking(true);
      const payload = { limit: '1000000', page_number: 1, product_id: productId };
      const response = await getProductInformation(payload);
      if (response?.data?.success || response?.data?.data) {
        const trackingData = response.data.data || response.data.trackingDetails || [];
        // Filter out lot tracking items - only show product-level tracking (status !== "lot" or missing status)
        const filteredData = Array.isArray(trackingData) 
          ? trackingData.filter(item => item.status !== "lot")
          : [];
        setTrackingDetails(filteredData);
      }
    } catch (error) {
      console.error("Error fetching tracking details:", error);
      setTrackingDetails([]);
    } finally {
      setIsLoadingTracking(false);
    }
  };

  useEffect(() => {
    fetchTrackingDetails();
  }, [productId]);

  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const effectiveActionStart = actionStart || productData?.action_start || productData?.actionStart || productData?.active_auction?.[0]?.acution_start_time || productData?.active_auction?.[0]?.auction_start_time;
  const effectiveActionEnd = actionEnd || productData?.action_end || productData?.actionEnd || productData?.active_auction?.[0]?.acution_end_time || productData?.active_auction?.[0]?.auction_end_time;

  const startMs = effectiveActionStart ? new Date(effectiveActionStart).getTime() : NaN;
  const endMs = effectiveActionEnd ? new Date(effectiveActionEnd).getTime() : NaN;
  const isStartValid = Number.isFinite(startMs);
  const isEndValid = Number.isFinite(endMs);

  let status = 'unknown';
  let timeLeft = 0;
  if (isStartValid && isEndValid) {
    if (nowMs < startMs) {
      status = 'upcoming';
      timeLeft = Math.max(0, startMs - nowMs);
    } else if (nowMs > endMs) {
      status = 'ended';
      timeLeft = 0;
    } else {
      status = 'live';
      timeLeft = Math.max(0, endMs - nowMs);
    }
  }

  const dd = Math.floor(timeLeft / 86400000);
  const hh = Math.floor((timeLeft % 86400000) / 3600000);
  const mm = Math.floor((timeLeft % 3600000) / 60000);
  const ss = Math.floor((timeLeft % 60000) / 1000);
  const countdown = status === 'unknown' ? '—' : status === 'ended' ? 'Ended' : `${dd > 0 ? dd + 'd ' : ''}${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

  const fmtDT = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const events = mapTrackingToEvents(trackingDetails);
  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));
  const expandAllPhase = (recordId, open) => setExpanded((m) => ({ ...m, [recordId]: open }));

  const handleCoordinatesChange = ({ location, coordinates }) => {
    setLocationData({ location });
    setCoordinates(coordinates);
    setFormData(prev => ({ ...prev, location }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => setImages(Array.from(e.target.files || []));
  const handleDocumentChange = (e) => setDocuments(Array.from(e.target.files || []));

  const addTrackingDetails = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.batch_number) {
      toast.error("Please fill all required fields");
      return;
    }
    if (images.length === 0) {
      toast.error("Please add at least one image");
      return;
    }
    if (!coordinates) {
      toast.error("Please get your location or enter it manually");
      return;
    }
    try {
      setIsLoading(true);
      const trackingFormData = new FormData();
      trackingFormData.append("product_id", productId);
      trackingFormData.append("title", formData.title);
      trackingFormData.append("description", formData.description);
      trackingFormData.append("origin", locationData?.location || formData.location);
      trackingFormData.append("batch_number", formData.batch_number);
      trackingFormData.append("location", JSON.stringify(coordinates));
      images.forEach((image) => trackingFormData.append("File", image));
      documents.forEach((document) => trackingFormData.append("File2", document));
      const res = await addProductTrackingDetail(trackingFormData);
      toast.success(res?.data?.Message || "Tracking detail added successfully!");
      setFormData({ title: "", description: "", batch_number: "", location: "" });
      setImages([]);
      setDocuments([]);
      setCoordinates(null);
      setLocationData(null);
      setShowTrackingForm(false);
      await fetchTrackingDetails();
    } catch (error) {
      toast.error(error?.response?.data?.Message || "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDescription = async (e) => {
    e.preventDefault();
    if (!descriptionFormData.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (!selectedRecordId) {
      toast.error("No record selected");
      return;
    }
    try {
      setIsSubmittingDescription(true);
      const formData = new FormData();
      formData.append("record_id", selectedRecordId);
      formData.append("location", descriptionLocationData?.location || descriptionFormData.location || "");
      formData.append("description", descriptionFormData.description);
      descriptionFiles.forEach((file) => formData.append("File", file));
      const res = await addTrackingDescription(formData);
      toast.success(res?.data?.Message || "Tracking description added successfully!");
      setDescriptionFormData({ description: "", location: "" });
      setDescriptionFiles([]);
      setDescriptionLocationData(null);
      setDescriptionCoordinates(null);
      setShowDescriptionSidebar(false);
      setSelectedRecordId(null);
      await fetchTrackingDetails();
    } catch (error) {
      toast.error(error?.response?.data?.Message || "Something went wrong!");
    } finally {
      setIsSubmittingDescription(false);
    }
  };

  const handleAddCertification = async (e) => {
    e.preventDefault();
    if (!certificationFormData.title.trim()) {
      toast.error("Please enter a certificate name");
      return;
    }
    if (certificationFiles.length === 0) {
      toast.error("Please select at least one certification file");
      return;
    }
    if (!selectedRecordId) {
      toast.error("No record selected");
      return;
    }
    try {
      setIsSubmittingCertification(true);
      const formData = new FormData();
      formData.append("record_id", selectedRecordId);
      formData.append("certificate_name", certificationFormData.title);
      formData.append("location", certificationLocationData?.location || certificationFormData.location || "");
      certificationFiles.forEach((file) => formData.append("File", file));
      const res = await addCertificate(formData);
      if (res.status === 200) {
        toast.success("Certification data added successfully!");
      } else {
        toast.error("Failed to add certification data.");
      }
      setCertificationFiles([]);
      setCertificationFormData({ title: "", description: "", location: "" });
      setCertificationLocationData(null);
      setCertificationCoordinates(null);
      setShowCertificationSidebar(false);
      setSelectedRecordId(null);
      await fetchTrackingDetails();
    } catch (error) {
      console.error("Error uploading certification:", error);
      toast.error(error?.response?.data?.Message || "An error occurred while uploading certification.");
    } finally {
      setIsSubmittingCertification(false);
    }
  };

  const fetchTrackingHistory = async (recordId) => {
    if (!recordId) return;
    try {
      setIsLoadingHistory(true);
      const payload = { limit: '1000000', page_number: 1, product_id: productId };
      const response = await getProductInformation(payload);
      if (response?.data?.success || response?.data?.data) {
        const trackingData = response.data.data || response.data.trackingDetails || [];
        // Filter out lot tracking items - only show product-level tracking
        const filteredData = Array.isArray(trackingData) 
          ? trackingData.filter(item => item.status !== "lot")
          : [];
        const selectedRecord = filteredData.find(record => record._id === recordId);
        if (selectedRecord) {
          const descriptions = Array.isArray(selectedRecord.tracking_history)
            ? selectedRecord.tracking_history.filter(item => item.type === 'description' || !item.certificate_name)
            : [];
          const certificates = Array.isArray(selectedRecord.tracking_history)
            ? selectedRecord.tracking_history.filter(item => item.certificate_name)
            : [];
          setTrackingHistory({ descriptions, certificates });
        } else {
          setTrackingHistory({ descriptions: [], certificates: [] });
        }
      } else {
        setTrackingHistory({ descriptions: [], certificates: [] });
      }
    } catch (error) {
      console.error("Error fetching tracking history:", error);
      toast.error("Failed to load tracking history");
      setTrackingHistory({ descriptions: [], certificates: [] });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleViewHistory = async (recordId) => {
    setSelectedRecordForHistory(recordId);
    setShowHistoryModal(true);
    await fetchTrackingHistory(recordId);
  };

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <LoadingOverlay>
          <Ring size={65} color="#00f7d2" />
        </LoadingOverlay>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ margin: 0 }}>Traceability PRO</h3>
        {isProductOwner() && (
          <button
            onClick={() => setShowTrackingForm(!showTrackingForm)}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              background: showTrackingForm ? "#1f2937" : "linear-gradient(90deg, #00f7d2, #5ce9ff)",
              color: showTrackingForm ? "#9ca3af" : "#000",
              fontWeight: 600,
              fontSize: 14,
              border: "none",
              cursor: "pointer",
            }}
          >
            {showTrackingForm ? "Cancel" : "Add Tracking Detail"}
          </button>
        )}
      </div>



      {isLoadingTracking ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Ring size={40} color="#00f7d2" />
        </div>
      ) : events.length > 0 ? (
        events.map((e, eventIndex) => {
          console.log("🚀 ~ TraceabilityPROV4 ~ e:", e)
            return (
            <div key={e.id} style={{ marginBottom: 10 }}>
              <PhaseHeader
                icon={phaseMeta[e.phase]?.icon || '📦'}
                title={e.title || e.phase || 'Tracking Event'}
                hint={e?.detail || 'Tracking record'}
                eventCount={1}
                onExpand={() => expandAllPhase(e.id, true)}
                onCollapse={() => expandAllPhase(e.id, false)}
              />
              <TrackingEventCard
                event={e}
                isLast={eventIndex === events.length - 1}
                isExpanded={expanded[e.id]}
                isProductOwner={isProductOwner()}
                isMapLoaded={isMapLoaded}
                txBase={txBase}
                onToggle={() => toggle(e.id)}
                onViewHistory={handleViewHistory}
                onAddDescription={(recordId) => {
                  setSelectedRecordId(recordId);
                              setShowDescriptionSidebar(true);
                            }}
                onAddCertification={(recordId) => {
                  setSelectedRecordId(recordId);
                              setShowCertificationSidebar(true);
                            }}
              />
                  </div>
          );
        })
      ) : (
        <p style={{ opacity: 0.75 }}>No tracking records found.</p>
      )}

      {showTrackingForm && (
        <FormContainer>
          <h4 style={{ margin: "0 0 20px 0", color: "#e5e7eb" }}>Add Tracking Detail</h4>
          <form onSubmit={addTrackingDetails}>
            <FormGroup>
              <Label>Title *</Label>
              <Input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Enter tracking title" required />
            </FormGroup>
            <FormGroup>
              <Label>Description *</Label>
              <TextArea name="description" value={formData.description} onChange={handleInputChange} placeholder="Enter tracking description" required />
            </FormGroup>
            <FormGroup>
              <Label>Batch Number *</Label>
              <Input type="text" name="batch_number" value={formData.batch_number} onChange={handleInputChange} placeholder="Enter batch number" required />
            </FormGroup>
            <FormGroup>
              <Label>Location</Label>
              <LocationSearch onCoordinatesChange={handleCoordinatesChange} required isGoogleMapsLoaded={isMapLoaded} />
              {coordinates && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
      </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Images *</Label>
              <FileInputWrapper>
                <FileInputLabel>
                  {images.length > 0 ? `${images.length} file(s) selected` : "Choose Images"}
                  <FileInput type="file" accept="image/*" multiple onChange={handleImageChange} />
                </FileInputLabel>
                {images.length > 0 && (
                  <FileList>
                    {images.map((file, index) => (
                      <div key={index}>• {file.name}</div>
                    ))}
                  </FileList>
                )}
              </FileInputWrapper>
            </FormGroup>
            <FormGroup>
              <Label>Documents</Label>
              <FileInputWrapper>
                <FileInputLabel>
                  {documents.length > 0 ? `${documents.length} file(s) selected` : "Choose Documents"}
                  <FileInput type="file" accept=".pdf,.doc,.docx,.txt" multiple onChange={handleDocumentChange} />
                </FileInputLabel>
                {documents.length > 0 && (
                  <FileList>
                    {documents.map((file, index) => (
                      <div key={index}>• {file.name}</div>
                    ))}
                  </FileList>
                )}
              </FileInputWrapper>
            </FormGroup>
            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? "Submitting..." : "Submit Tracking Detail"}
            </SubmitButton>
          </form>
        </FormContainer>
      )}

      {/* Description Sidebar */}
      <SidebarOverlay show={showDescriptionSidebar} onClick={() => setShowDescriptionSidebar(false)} />
      <DescriptionSidebar open={showDescriptionSidebar}>
        <SidebarHeader>
          <SidebarTitle>Add Tracking Description</SidebarTitle>
          <CloseButton onClick={() => setShowDescriptionSidebar(false)}>×</CloseButton>
        </SidebarHeader>
        <form onSubmit={handleAddDescription} style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
          <FormGroup>
            <Label>Description *</Label>
            <TextArea name="description" value={descriptionFormData.description} onChange={(e) => setDescriptionFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Enter description" required />
          </FormGroup>
          <FormGroup>
            <Label>Location</Label>
            <LocationSearch
              onCoordinatesChange={(data) => {
                setDescriptionLocationData(data);
                setDescriptionFormData(prev => ({ ...prev, location: data.location || "" }));
                setDescriptionCoordinates(data.coordinates);
              }}
              isGoogleMapsLoaded={isMapLoaded}
            />
            {descriptionCoordinates && (
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                Coordinates: {descriptionCoordinates.lat.toFixed(6)}, {descriptionCoordinates.lng.toFixed(6)}
              </div>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Files</Label>
            <FileInputWrapper>
              <FileInputLabel>
                {descriptionFiles.length > 0 ? `${descriptionFiles.length} file(s) selected` : "Choose Files"}
                <FileInput type="file" multiple onChange={(e) => setDescriptionFiles(Array.from(e.target.files || []))} />
              </FileInputLabel>
              {descriptionFiles.length > 0 && (
                <FileList>
                  {descriptionFiles.map((file, index) => (
                    <div key={index}>• {file.name}</div>
                  ))}
                </FileList>
              )}
            </FileInputWrapper>
          </FormGroup>
          <SubmitButton type="submit" disabled={isSubmittingDescription || !descriptionFormData.description.trim()}>
            {isSubmittingDescription ? "Submitting..." : "Submit Description"}
          </SubmitButton>
        </form>
      </DescriptionSidebar>

      {/* Certification Sidebar */}
      <SidebarOverlay show={showCertificationSidebar} onClick={() => setShowCertificationSidebar(false)} />
      <CertificationSidebar open={showCertificationSidebar}>
        <SidebarHeader>
          <SidebarTitle>Add Certification</SidebarTitle>
          <CloseButton onClick={() => setShowCertificationSidebar(false)}>×</CloseButton>
        </SidebarHeader>
        <form onSubmit={handleAddCertification} style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
          <FormGroup>
            <Label>Certificate Name *</Label>
            <TextArea name="title" value={certificationFormData.title} onChange={(e) => setCertificationFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Enter certificate name" required rows={2} />
          </FormGroup>
          <FormGroup>
            <Label>Location</Label>
            <LocationSearch
              onCoordinatesChange={(data) => {
                setCertificationLocationData(data);
                setCertificationFormData(prev => ({ ...prev, location: data.location || "" }));
                setCertificationCoordinates(data.coordinates);
              }}
              isGoogleMapsLoaded={isMapLoaded}
            />
            {certificationCoordinates && (
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                Coordinates: {certificationCoordinates.lat.toFixed(6)}, {certificationCoordinates.lng.toFixed(6)}
              </div>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Certification Files *</Label>
            <FileInputWrapper>
              <FileInputLabel>
                {certificationFiles.length > 0 ? `${certificationFiles.length} file(s) selected` : "Choose Files"}
                <FileInput type="file" multiple onChange={(e) => setCertificationFiles(Array.from(e.target.files || []))} />
              </FileInputLabel>
              {certificationFiles.length > 0 && (
                <FileList>
                  {certificationFiles.map((file, index) => (
                    <div key={index}>• {file.name}</div>
                  ))}
                </FileList>
              )}
            </FileInputWrapper>
          </FormGroup>
          <SubmitButton type="submit" disabled={isSubmittingCertification || certificationFiles.length === 0 || !certificationFormData.title.trim()}>
            {isSubmittingCertification ? "Submitting..." : "Submit Certification"}
          </SubmitButton>
        </form>
      </CertificationSidebar>

      {/* Tracking History Sidebar */}
      <TrackingHistorySidebar
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        isLoading={isLoadingHistory}
        trackingHistory={trackingHistory}
      />
    </div>
  );
}

export default TraceabilityPROV4;
